import React, { useState } from "react";
import "./index.css";
import {
  CartLoading,
  CartLoadingAllocation,
  CartLoadingDetailResponse,
  Gate,
} from "../../../../redux/cartLoadingSlice";
import LoadingManagement from "../LoadingManagement";
import CartsList from "../Cart List";
import OrderSelection from "../OrderSelection";
import GroupedOrdersView from "../GroupsOrderView";

interface OrderGroup {
  id: string;
  name: string;
  orders: string[];
  color: string;
}

interface SupervisorViewProps {
  supervisorTab: string;
  setSupervisorTab: (tab: string) => void;
  carts: CartLoading[];
  gates: Gate[];
  cartAllocations: CartLoadingAllocation[];
  cart: CartLoading | undefined;
  getStatusColor: (status: string) => string;
  getStatusText: (status: string) => string;
  formatTime: (timestamp: string | null) => string;
  getTimeRemaining: (targetTime: string | null) => string;
  updateCartState: (cartId: string, status: string, gateId?: string, groupedOrderIds?: string[]) => void;
  setSelectedCart: (cartId: string) => void;
  setSelectedCartForOrderSelection: (cartId: string) => void;
  cartDetailData?: CartLoadingDetailResponse;
  isCartDetailLoading: boolean;
  viewingCart: string;
  isDetailLoading: boolean;
  updatingCarts: Record<string, string>;
  orderGroups: OrderGroup[];
  onProceedToGroupedOrders: (groups: OrderGroup[]) => void;
  onConfirmGroups: () => Promise<void>;
  onBackToOrderSelection: () => void;
  onBackToCartList: () => void;
  isCreatingGroupOrder?: boolean;
  isInitialLoading?: boolean;
  hasError?: boolean;
  onRefreshCarts?: () => Promise<void>;
  isRefreshing?: boolean;
}

const SupervisorView: React.FC<SupervisorViewProps> = ({
  supervisorTab,
  setSupervisorTab,
  carts,
  cartAllocations,
  gates,
  cart,
  getStatusColor,
  getStatusText,
  formatTime,
  getTimeRemaining,
  updateCartState,
  setSelectedCart,
  setSelectedCartForOrderSelection,
  cartDetailData,
  isCartDetailLoading,
  viewingCart,
  isDetailLoading,
  updatingCarts,
  orderGroups,
  onProceedToGroupedOrders,
  onConfirmGroups,
  onBackToOrderSelection,
  onBackToCartList,
  isCreatingGroupOrder,
  isInitialLoading = false,
  hasError = false,
  onRefreshCarts,
  isRefreshing = false,
}) => {
  const [orderSelectionLoadingCartId, setOrderSelectionLoadingCartId] =
    useState<string | null>(null);

  const handleSetSelectedCartForOrderSelection = (cartId: string) => {
    setOrderSelectionLoadingCartId(cartId);
    try {
      setSelectedCartForOrderSelection(cartId);
      setSupervisorTab("order-selection");
    } finally {
      setTimeout(() => setOrderSelectionLoadingCartId(null), 1000);
    }
  };

  const getTabTitle = () => {
    switch (supervisorTab) {
      case "carts":
        return "Cart Loading Management";
      case "gates":
        return "Gates & Racks Overview";
      case "order-selection":
        return `Order Selection - ${cart?.displayName || cart?.id}`;
      case "grouped-orders":
        return `Grouped Orders - ${cart?.displayName || cart?.id}`;
      case "loading":
        return `Loading Management - ${cart?.displayName || cart?.id}`;
      default:
        return "Cart Loading Management";
    }
  };

  const getOrdersForSelection = () => {
    if (cartDetailData) {
      const groupedOrderIds = new Set(
        cartDetailData.groupOrderDetailsList?.flatMap((group) =>
          group.orders.map((order) => order.id)
        ) || []
      );
      return (cartDetailData.ordersForCart || []).filter(
        (order) => !groupedOrderIds.has(order.id)
      );
    }
    return cart?.orders || [];
  };

  const ordersForSelection = getOrdersForSelection();

  // Show initial loading state
  if (isInitialLoading) {
    return (
      <div className="supervisor-view-container">
        <div className="supervisor-view-header">
          <div className="supervisor-view-title">
            <h2 className="supervisor-view-heading">Loading Cart Data...</h2>
          </div>
        </div>
        <div className="loading-management-loading">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
        </div>
      </div>
    );
  }

  // Show error state
  if (hasError) {
    return (
      <div className="supervisor-view-container">
        <div className="supervisor-view-header">
          <div className="supervisor-view-title">
            <h2 className="supervisor-view-heading">Error Loading Carts</h2>
          </div>
        </div>
        <div className="error-message-component">
          <p>Failed to load cart data. Please try again.</p>
          <button 
            onClick={() => window.location.reload()} 
            className="btn btn-primary mt-3"
          >
            Refresh Page
          </button>
        </div>
      </div>
    );
  }

  const EmptyCartsState = () => (
    <div className="no-carts-message">
      <div className="no-carts-icon">
        <span>🛒</span>
      </div>
      <h3>No Carts Available</h3>
      <p>There are no carts in the loading queue at the moment.</p>
    </div>
  );

  return (
    <div className="supervisor-view-container">
      <div className="supervisor-view-header">
        <div className="supervisor-view-title">
          <h2 className="supervisor-view-heading">{getTabTitle()}</h2>
        </div>
      
      </div>

      {supervisorTab === "carts" && (
        <>
          {carts.length === 0 ? (
            <EmptyCartsState />
          ) : (
            <CartsList
              carts={carts}
              cartAllocations={cartAllocations}
              gates={gates}
              getStatusColor={getStatusColor}
              getStatusText={getStatusText}
              formatTime={formatTime}
              getTimeRemaining={getTimeRemaining}
              updateCartState={updateCartState}
              setSelectedCart={setSelectedCart}
              setSelectedCartForOrderSelection={handleSetSelectedCartForOrderSelection}
              setSupervisorTab={setSupervisorTab}
              viewingCart={viewingCart}
              isDetailLoading={isDetailLoading}
              updatingCarts={updatingCarts}
              orderSelectionLoadingCartId={orderSelectionLoadingCartId}
              onRefreshCarts={onRefreshCarts}
              isRefreshing={isRefreshing}
            />
          )}
        </>
      )}

      {supervisorTab === "order-selection" && cart && (
        <OrderSelection
          orders={ordersForSelection}
          onProceedToGroupedOrders={onProceedToGroupedOrders}
          onBackToCartList={onBackToCartList}
          cartId={cart.id}
        />
      )}

      {supervisorTab === "grouped-orders" && cart && (
        <GroupedOrdersView
          orderGroups={orderGroups}
          orders={cart.orders || []}
          cart={cart}
          onBackToOrderSelection={onBackToOrderSelection}
          onConfirmGroups={onConfirmGroups}
          isCreatingGroupOrder={isCreatingGroupOrder}
          setSupervisorTab={setSupervisorTab}
          updateCartState={updateCartState} 
        />
      )}

      {supervisorTab === "loading" && cart && (
        <LoadingManagement
          cart={cart}
          cartAllocations={cartAllocations}
          gates={gates}
          getStatusColor={getStatusColor}
          getStatusText={getStatusText}
          getTimeRemaining={getTimeRemaining}
          updateCartState={updateCartState}
          setSupervisorTab={setSupervisorTab}
          setSelectedCart={setSelectedCart}
          cartDetailData={cartDetailData}
          isCartDetailLoading={isCartDetailLoading}
          updatingCarts={updatingCarts}
        />
      )}
    </div>
  );
};

export default SupervisorView;